import { Accordion, AccordionSummary, Alert, Stack } from '@mui/material';
import styles from './DocumentExplorer.module.css';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import FlakyIcon from '@mui/icons-material/Flaky';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

export function DocumentExplorer(
    {
        documents,
        onDocumentClick,
        onDatapointClick,
        selectedDocumentId,
        selectedDatapointId,
    },
) {
    const theme = useTheme();
    const [expandedDocumentId, setExpandedDocumentId] = useState(null);

    const isDatapointSelected = (documentId, datapointId) =>
        datapointId === selectedDatapointId
        && documentId === selectedDocumentId;

    useEffect(() => {
        const selectedDocumentIndex = documents.findIndex((doc) => doc.id === selectedDocumentId);

        if (selectedDocumentIndex !== -1) {
            setExpandedDocumentId(selectedDocumentIndex);
        }
    }, [selectedDocumentId, documents, selectedDatapointId]);

    const handleChangeExpanded = (panel) => (event, isExpanded) => {
        setExpandedDocumentId(isExpanded ? panel : null);
    };

    const statusIcon = (status, isSelected) => {
        const statusIcons = {
            'Backlog': PanoramaFishEyeIcon,
            'In Progress': FlakyIcon,
            'Completed': CheckCircleOutlineIcon,
        };
        const Icon = statusIcons[status];

        return (
            <Icon
                sx={{ color: isSelected ? theme.palette.primary.contrastText : theme.palette.primary.main }}
            />
        );
    };

    const documentRow = (document, datapoints, index) => {
        return (
            <Accordion
                key={`accordion-${index}`}
                expanded={expandedDocumentId === index}
                onChange={handleChangeExpanded(index)}
                style={
                    documents.findIndex((doc) => doc.id === selectedDocumentId) === index
                        ? {
                            border: '1px solid',
                            'border-color': theme.palette.primary.main,
                        }
                        : {}
                }
                sx={{
                    '&.MuiAccordion-root': {
                        width: '100%',
                        borderRadius: '4px',
                    },
                    '&.MuiAccordion-root::before': {
                        display: 'none',
                    },
                    '& .MuiAccordionSummary-root.Mui-expanded': {
                        borderBottom: '2px solid #F8F8F8',
                    },
                    '&.Mui-expanded': {
                        margin: '0',
                    },
                    '.MuiAccordionSummary-contentGutters': {
                        overflow: 'hidden',
                    },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-c-${index}`}
                    id={`panel-h-${index}`}
                    sx={{
                        overflow: 'hidden',
                    }}
                    onClick={() => onDocumentClick?.(document.id)}
                >
                    <div className={styles.documentTitle}>
                        <Tooltip title={document.name}>
                            {document.name}
                        </Tooltip>
                    </div>
                    {statusIcon(document.status)}
                </AccordionSummary>
                <AccordionDetails>
                    <div className={styles.documents}>
                        {datapoints.map((datapoint, index) => (
                            <div
                                key={index}
                                className={`${styles.datapoint} ${isDatapointSelected(document.id, datapoint.id)
                                    ? styles.selectedDatapoint
                                    : ''}`}
                                onClick={() => onDatapointClick?.(document.id, datapoint.id)}
                            >
                                <Tooltip title={datapoint.name}>
                                    {datapoint.name}
                                </Tooltip>
                                {statusIcon(datapoint.status, isDatapointSelected(document.id, datapoint.id))}
                            </div>
                        ))}
                    </div>
                </AccordionDetails>
            </Accordion>
        );
    };

    return !documents?.length
        ? (
            <Stack sx={{ width: '100%' }} spacing={2}>
                <Alert severity="warning">No documents</Alert>
            </Stack>
        ) : (
            <div className={styles.container}>
                {documents.map((document, index) => documentRow(document, document.datapoints, index))}
            </div>
        );
}

DocumentExplorer.propTypes = {};
